import { useEffect } from 'react';
import { PlayableInvoice } from '@/enterprise/data/usePlayableInvoices';
import { useEditInvoicesContext } from './EditInvoicesContext';

export interface UseSelectedInvoicesProps {
  currentCursor: string | null;
  invoices?: PlayableInvoice[];
  tableType: 'included' | 'excluded';
}

/**
 *
 * This checks if the invoice is:
 * 1. Partially adjusted and is covering an unmatched adjustment
 * 2. Has an amount greater than 0
 * 3. Is currently included (takerExcluded is false)
 *
 * TakerExcluded check was added to this because we want to ALLOW re-inclusion of an invoice that has been
 * taker excluded and covers a partial adjustment, but we NEVER want to allow excluding an invoice that covers
 * a partial adjustment.
 *
 * Scenario that required this additional check:
 * 1. Invoice is taker excluded.
 * 2. Invoice has a partial adjustment applied to it.
 * 3. The Market has the feature to automatically re-include invoices covering adjustments turned off
 * so the invoice is not automatically re-included.
 * 4. Without adding takerExcluded check we would not allow re-inclusion of this example invoice.
 *
 */
export const isPartiallyAdjustedInvoiceCoveringUnmatchedAdjustmentAndIncluded = (invoice: PlayableInvoice) =>
  invoice.coversUnmatched && invoice.amount > 0 && invoice.supplierExcluded === false;

const useSelectedInvoices = ({ currentCursor, invoices, tableType }: UseSelectedInvoicesProps) => {
  const { dispatch, state } = useEditInvoicesContext();

  // reset when table type changes (included/excluded tabs)
  useEffect(() => {
    dispatch({ type: 'RESET_SELECTED' });
  }, [tableType, dispatch]);

  // reset when page changes
  useEffect(() => {
    dispatch({ type: 'RESET_SELECTED' });
  }, [currentCursor, dispatch]);

  // multi-page selection logic
  const currentPageInvoiceIds = invoices?.map((invoice) => invoice.voucherId) ?? [];
  const selectedInvoiceIds = state.selected.map((invoice) => invoice.voucherId);
  const currentPageSelectedCount = currentPageInvoiceIds.filter((id) => selectedInvoiceIds.includes(id)).length;
  const totalCurrentPageInvoices = currentPageInvoiceIds.length;

  // selection state based on mode
  const isAllInvoicesSelected = state.selectionMode === 'all'; // true when all invoices across all pages are selected (not just current page)
  // true when all invoices on the current page are selected (regardless of selection mode)
  const isAllCurrentPageSelected =
    state.selectionMode === 'all' || // all invoices selected globally
    (state.selectionMode === 'page' &&
      totalCurrentPageInvoices > 0 &&
      currentPageSelectedCount === totalCurrentPageInvoices) || // full page explicitly selected
    (state.selectionMode === 'some' &&
      totalCurrentPageInvoices > 0 &&
      currentPageSelectedCount === totalCurrentPageInvoices); // individual selection happened to select all on page
  // true when some (not all) invoices on the current page are selected
  const isSomeCurrentPageSelected =
    state.selectionMode === 'some' &&
    currentPageSelectedCount > 0 &&
    currentPageSelectedCount < totalCurrentPageInvoices;

  // header checkbox states
  const isHeaderIndeterminate = isSomeCurrentPageSelected;
  const isHeaderChecked = isAllInvoicesSelected || isAllCurrentPageSelected;

  // header checkbox change handler
  const handleHeaderCheckboxChange = () => {
    if (state.selectionMode === 'all') {
      // reset all selections when in 'all' mode
      dispatch({ type: 'RESET_SELECTED' });
    } else if (isAllCurrentPageSelected) {
      // deselect all current page invoices
      const currentPageInvoices = invoices?.filter((invoice) => selectedInvoiceIds.includes(invoice.voucherId)) ?? [];
      currentPageInvoices.forEach((invoice) => {
        dispatch({ type: 'TOGGLE_SELECTED', payload: { invoice } });
      });
    } else {
      // select all current page invoices
      dispatch({ type: 'SELECT_ALL_PAGE', payload: { invoices: invoices ?? [] } });
    }
  };

  // individual invoice checkbox handler
  const handleInvoiceCheckboxChange = (invoice: PlayableInvoice) => {
    dispatch({ type: 'TOGGLE_SELECTED', payload: { invoice } });
  };

  // check if individual invoice is selected
  const isInvoiceSelected = (invoice: PlayableInvoice) => {
    // If the invoice is covering an unmatched adjustment and included (taker excluded is false), it should not be checked
    // This is because it is okay to include an invoice that is covering an unmatched adjustment but never to exclude it
    if (isPartiallyAdjustedInvoiceCoveringUnmatchedAdjustmentAndIncluded(invoice)) {
      return false;
    }

    return (
      state.selectionMode === 'all' ||
      state.selected.some((selectedInvoice) => selectedInvoice.voucherId === invoice.voucherId)
    );
  };

  // select all invoices handler
  const handleSelectAllInvoices = () => {
    dispatch({ type: 'SELECT_ALL' });
  };

  return {
    state,
    isAllInvoicesSelected,
    isAllCurrentPageSelected,
    isHeaderIndeterminate,
    isHeaderChecked,
    handleHeaderCheckboxChange,
    handleInvoiceCheckboxChange,
    handleSelectAllInvoices,
    isInvoiceSelected,
  };
};

export default useSelectedInvoices;
